import { config } from '../config';
import { auth } from '../config/firebase';

const API_BASE_URL = config.server.backendUrl || 'http://localhost:8000';

/**
 * Get Firebase auth token
 */
const getAuthToken = async (): Promise<string | null> => {
  const user = auth.currentUser;
  if (!user) {
    return null;
  }
  return await user.getIdToken();
};

export interface UserProfile {
  id: string;
  firebaseUid: string;
  email: string;
  name: string;
  role: 'admin' | 'user';
  isActive: boolean;
  lastLogin?: string;
  preferences?: {
    theme: 'light' | 'dark';
    notifications: {
      email: boolean;
      push: boolean;
    };
  };
  createdAt: string;
  updatedAt: string;
}

/**
 * Get current user profile
 */
export const getCurrentUser = async (): Promise<UserProfile | null> => {
  const token = await getAuthToken();
  if (!token) {
    return null;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      console.error('Failed to fetch user profile:', await response.text());
      return null;
    }

    const data = await response.json();
    return data.data.user;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
};

/**
 * Check if user has a specific role
 */
export const checkUserRole = async (requiredRole: 'admin' | 'user'): Promise<boolean> => {
  const userProfile = await getCurrentUser();
  if (!userProfile) {
    return false;
  }

  // Admin role has access to everything
  if (userProfile.role === 'admin') {
    return true;
  }

  // User role only has access to user areas
  return userProfile.role === requiredRole;
};